import React, { useEffect, useState } from 'react';

/**
 * ScrollIcons Component
 * Features:
 * - Replaced external icon library with inline SVGs to ensure stability.
 * - Water Bubble hover effect on individual icons.
 * - Fixed sidebar that appears after scrolling 100px.
 * - Fully responsive using clamp() and media queries.
 */

const ScrollSocialIcons = () => {
  const [showIcons, setShowIcons] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowIcons(scrollTop > 100); // Show after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Using Inline SVGs to avoid dependency resolution issues
  const socialLinks = [
    { 
      id: 1, 
      icon: <svg viewBox="0 0 320 512" width="18" height="18" fill="currentColor"><path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/></svg>, 
      url: 'https://www.facebook.com/ciappvtltd' 
    },
    { 
      id: 2, 
      icon: <svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/></svg>, 
      url: 'https://www.instagram.com/ciappvtltd/' 
    },
    { 
      id: 3, 
      icon: <svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor"><path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"/></svg>, 
      url: 'https://www.linkedin.com/company/ciap-pvt-ltd/' 
    },
    { 
      id: 4, 
      icon: <svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor"><path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25V349.38A162.55 162.55 0 1 1 185 188.31V278.2a74.62 74.62 0 1 0 52.23 71.18V0l88 0a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14z"/></svg>, 
      url: 'https://www.tiktok.com/@ciap.pvt.ltd' 
    },
  ];

  return (
    <>
      <style>{`
        .scroll-icons-container {
          position: fixed;
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          background: rgb(1, 48, 118);
          border-left: 2px solid rgba(204, 204, 204, 0.3);
          padding: clamp(5px, 1vw, 10px) clamp(4px, 0.8vw, 8px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: clamp(8px, 1.5vh, 12px);
          box-shadow: -2px 0 15px rgba(0, 0, 0, 0.3);
          opacity: 0;
          pointer-events: none;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          border-radius: 10px 0 0 10px;
        }

        .scroll-icons-container.visible {
          opacity: 1;
          pointer-events: auto;
        }

        .scroll-icon-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .scroll-icons-container a {
          position: relative;
          color: #f7f5f5;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(8px, 1.2vw, 12px);
          touch-action: manipulation;
          z-index: 2;
          text-decoration: none;
        }

        /* --- WATER BUBBLE EFFECT --- */
        .bubble-effect {
          position: absolute;
          width: 0;
          height: 0;
          background: rgba(145, 209, 243, 0.4);
          border-radius: 50%;
          z-index: 1;
          transition: all 0.4s ease;
          pointer-events: none;
          filter: blur(2px);
        }

        .scroll-icon-wrapper:hover .bubble-effect {
          width: 48px;
          height: 48px;
          animation: bubble-float 2s ease-in-out infinite alternate;
        }

        .scroll-icon-wrapper:hover a {
          color: #ffffff;
          transform: scale(1.2);
          text-shadow: 0 0 8px rgba(145, 209, 243, 0.8);
        }

        @keyframes bubble-float {
          0% {
            transform: scale(1) translateY(0);
            border-radius: 50% 50% 50% 50%;
          }
          50% {
            transform: scale(1.1) translateY(-3px);
            border-radius: 40% 60% 50% 50%;
          }
          100% {
            transform: scale(1.05) translateY(2px);
            border-radius: 60% 40% 60% 40%;
          }
        }

        /* Media Queries for Fine-Tuning Icons */
        @media (max-width: 768px) {
          .scroll-icons-container {
            padding: clamp(4px, 0.8vw, 8px) clamp(3px, 0.6vw, 6px);
            gap: clamp(6px, 1vh, 10px);
          }
          .scroll-icon-wrapper:hover .bubble-effect {
            width: 38px;
            height: 38px;
          }
          .scroll-icons-container a svg {
             width: 16px;
             height: 16px;
          }
        }

        @media (max-width: 600px) {
          .scroll-icons-container {
            padding: clamp(3px, 0.5vw, 5px) clamp(2px, 0.4vw, 4px);
            gap: clamp(4px, 0.8vh, 8px);
            right: 0;
          }
          .scroll-icons-container a svg {
             width: 14px;
             height: 14px;
          }
        }
      `}</style>

      <div className={`scroll-icons-container ${showIcons ? 'visible' : ''}`}>
        {socialLinks.map((link) => (
          <div key={link.id} className="scroll-icon-wrapper">
            <div className="bubble-effect"></div>
            <a 
              href={link.url} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              {link.icon}
            </a>
          </div>
        ))}
      </div>
    </>
  );
};

export default ScrollSocialIcons;